import { useCallback } from "react";
import { eventBus } from "../common/eventBus";
import { ServerMessages, useSocketContext } from "../context/SocketContext";
import { Criteria, Options, EntriesData, GetLicensePlateEntriesResponse, EntriesPerMonth, IrrelevantEntry } from "../types/Entries";

const useEntriesService = () => {
    const { emit } = useSocketContext();

    const getEntries = useCallback(async (
        criteria: Criteria,
        options: Options
    ): Promise<EntriesData> => {
        const { minDate, maxDate, licensePlate } = criteria;

        const newCriteria = {
            ...criteria,
            minDate: minDate ? new Date(criteria.minDate).setHours(0, 0, 0, 0) : null,
            maxDate: maxDate ? new Date(criteria.maxDate).setHours(23, 59, 59, 999) : null,
            licensePlate: licensePlate ? `*${licensePlate}*` : '**',
        };

        const response = await emit<EntriesData>({
            message: ServerMessages.getEntries,
            criteria: newCriteria,
            options,
        });

        return response?.data ?? { entries: [], totalCount: 0 };
    }, [emit]);

    const getLicensePlateEntries = useCallback(async (
        licensePlate: string,
    ): Promise<GetLicensePlateEntriesResponse> => {
        const entries = await emit<EntriesData>({
            message: ServerMessages.getEntries,
            criteria: {
                licensePlate: `*${licensePlate}*`,
                minDate: null,
                maxDate: null,
            }
        });

        const entriesPerMonth = await emit<EntriesPerMonth>({
            message: ServerMessages.getReportEntriesPerMonth,
            criteria: {
                licensePlate,
            },
            options: {
                numMonths: 12,
            }
        });

        return {
            entries: entries?.data ?? { entries: [], totalCount: 0 },
            entriesPerMonth: entriesPerMonth?.data ?? { countEntries: [], months: [] },
        };
    }, [emit]);

    const markCarAsIrrelevant = useCallback(async (licensePlate: string, irrelevant: boolean, reason?: string) => {
        await emit<void>({
            message: ServerMessages.markIrrelevant,
            licensePlate: licensePlate.trim(),
            irrelevant,
            reason,
        });

        eventBus.emit('carMarkedAsIrrelevant', { licensePlate, irrelevant });
    }, [emit]);

    const deleteEntry = useCallback(async (entryId: string) => {
        await emit<void>({
            message: ServerMessages.deleteEntry,
            entryId,
        });

        eventBus.emit('entryDeleted', { entryId });
    }, [emit]);

    const deleteAllEntriesForLicensePlate = useCallback(async (licensePlate: string) => {
        await emit<void>({
            message: ServerMessages.deleteAllEntriesForLicensePlate,
            licensePlate,
        });

        eventBus.emit('entryDeleted', { licensePlate });
    }, [emit]);

    const getIrrelevantLicensePlates = useCallback(async (): Promise<IrrelevantEntry[]> => {
        const response = await emit<IrrelevantEntry[]>({
            message: ServerMessages.getIrrelevantLicensePlates,
        });

        return response?.data ?? [];
    }, [emit]);

    return {
        getEntries,
        getLicensePlateEntries,
        markCarAsIrrelevant,
        deleteEntry,
        deleteAllEntriesForLicensePlate,
        getIrrelevantLicensePlates,
    };
};

export default useEntriesService;
